import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { FilePlus2Icon } from 'lucide-react'
import { useId, useState } from 'react'

import { CORPORATE_KEY, corporateApi } from '@/api/corporate'
import { ApiError } from '@/api/errors'
import type { CorporateBodyKind, Language, MinuteKind, MinuteTemplate } from '@/api/types'
import { TemplateCaution } from '@/components/corporate/template-caution'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useResetOnOpen } from '@/hooks/use-reset-on-open'
import { BODY_LABELS, MINUTE_KIND_LABELS, MINUTE_STATUS_LABELS } from '@/lib/corporate'
import { formatDateOnly } from '@/lib/format'
import { localToday } from '@/lib/portal-home'

export const Route = createFileRoute('/_app/corporate/resolutions/')({
  component: MinutesPage,
})

const STATUS_TONE = { draft: 'warning', adopted: 'success', superseded: 'muted' } as const

/**
 * Résolutions et procès-verbaux (lot 45) : les brouillons d'abord, puis les
 * adoptés, numérotés sans trou par organe et par année. Ce qui est adopté ne
 * se réécrit pas : on le remplace par une nouvelle pièce qui y renvoie.
 */
function MinutesPage() {
  const [creating, setCreating] = useState(false)
  const minutes = useQuery({
    queryKey: [...CORPORATE_KEY, 'minutes'],
    queryFn: () => corporateApi.minutes(),
  })
  const items = [...(minutes.data ?? [])].sort((a, b) => {
    if (a.status === 'draft' && b.status !== 'draft') return -1
    if (b.status === 'draft' && a.status !== 'draft') return 1
    return b.effective_on.localeCompare(a.effective_on)
  })
  return (
    <Frame className="space-y-4" data-testid="resolutions">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Résolutions et procès-verbaux</h2>
        <Button
          type="button"
          onClick={() => {
            setCreating(true)
          }}
        >
          <FilePlus2Icon />
          Nouvelle résolution
        </Button>
      </div>
      {minutes.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : minutes.isError ? (
        <FormError>Les résolutions n’ont pas pu être lues.</FormError>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucune résolution au livre.</p>
      ) : (
        <ul className="divide-y text-sm">
          {items.map((minute) => (
            <li key={minute.id} data-testid="resolution">
              <Link
                to="/corporate/resolutions/$minuteId"
                params={{ minuteId: minute.id }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 hover:bg-muted/40"
              >
                <span className="w-24 font-mono text-xs text-muted-foreground">
                  {minute.ref || '—'}
                </span>
                <span className="min-w-0 flex-1 font-medium">{minute.title}</span>
                <span className="text-muted-foreground">
                  {MINUTE_KIND_LABELS[minute.kind]} · {BODY_LABELS[minute.body]} ·{' '}
                  {formatDateOnly(minute.effective_on)}
                </span>
                <Badge variant={STATUS_TONE[minute.status]}>
                  {MINUTE_STATUS_LABELS[minute.status]}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <NewMinuteDialog open={creating} onOpenChange={setCreating} />
    </Frame>
  )
}

function NewMinuteDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const ids = { title: useId(), date: useId() }
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [template, setTemplate] = useState<MinuteTemplate | null>(null)
  const [kind, setKind] = useState<MinuteKind>('resolution')
  const [body, setBody] = useState<CorporateBodyKind>('shareholders')
  const [language, setLanguage] = useState<Language>('fr')
  const [title, setTitle] = useState('')
  const [date, setDate] = useState(localToday())
  useResetOnOpen(open, 'nouvelle-resolution', () => {
    setTemplate(null)
    setKind('resolution')
    setBody('shareholders')
    setLanguage('fr')
    setTitle('')
    setDate(localToday())
    create.reset()
  })
  const templates = useQuery({
    queryKey: [...CORPORATE_KEY, 'templates'],
    queryFn: corporateApi.templates,
    enabled: open,
  })
  const shown = (templates.data ?? []).filter((t) => t.language === language)
  const create = useMutation({
    mutationFn: () =>
      corporateApi.createMinute({
        kind,
        body: template?.body ?? body,
        title: title.trim() ? title.trim() : (template?.title ?? 'Résolution'),
        language,
        effective_on: date,
        template_key: template?.key ?? null,
      }),
    onSuccess: (minute) => {
      void queryClient.invalidateQueries({ queryKey: [...CORPORATE_KEY, 'minutes'] })
      onOpenChange(false)
      void navigate({ to: '/corporate/resolutions/$minuteId', params: { minuteId: minute.id } })
    },
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nouvelle résolution</DialogTitle>
          <DialogDescription>
            Partir d’un modèle, ou d’une page blanche. Le brouillon se modifie librement ; une fois
            adoptée, la résolution ne se modifie plus.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-3 text-sm">
          {(['resolution', 'minutes'] as const).map((value) => (
            <label key={value} className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`${ids.title}-kind`}
                className="accent-primary"
                checked={kind === value}
                onChange={() => {
                  setKind(value)
                }}
              />
              {MINUTE_KIND_LABELS[value]}
            </label>
          ))}
          <span className="text-muted-foreground">·</span>
          {(['fr', 'en'] as const).map((value) => (
            <label key={value} className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`${ids.title}-lang`}
                className="accent-primary"
                checked={language === value}
                onChange={() => {
                  setLanguage(value)
                  setTemplate(null)
                }}
              />
              {value === 'fr' ? 'Français' : 'English'}
            </label>
          ))}
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Modèle</legend>
          <ul className="max-h-64 space-y-1 overflow-y-auto">
            <li>
              <label className="flex items-start gap-2 rounded-md p-2 text-sm hover:bg-muted/50">
                <input
                  type="radio"
                  name={`${ids.title}-tpl`}
                  className="mt-1 accent-primary"
                  checked={template === null}
                  onChange={() => {
                    setTemplate(null)
                  }}
                />
                <span>Page blanche</span>
              </label>
            </li>
            {templates.isPending && open ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              shown.map((item) => (
                <li key={item.key}>
                  <label
                    htmlFor={`${ids.title}-tpl-${item.key}`}
                    aria-label={item.title}
                    className="flex items-start gap-2 rounded-md p-2 text-sm hover:bg-muted/50"
                    data-testid="modele"
                  >
                    <input
                      id={`${ids.title}-tpl-${item.key}`}
                      type="radio"
                      name={`${ids.title}-tpl`}
                      className="mt-1 accent-primary"
                      checked={template?.key === item.key}
                      onChange={() => {
                        setTemplate(item)
                        setBody(item.body)
                      }}
                    />
                    <span>
                      <span className="block font-medium">{item.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {BODY_LABELS[item.body]}
                        {item.legal_reference ? ` · ${item.legal_reference}` : ''}
                      </span>
                    </span>
                  </label>
                </li>
              ))
            )}
          </ul>
          {template && <TemplateCaution />}
        </fieldset>
        {template === null && (
          <div className="flex gap-3 text-sm">
            {(['shareholders', 'directors'] as const).map((value) => (
              <label key={value} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={`${ids.title}-body`}
                  className="accent-primary"
                  checked={body === value}
                  onChange={() => {
                    setBody(value)
                  }}
                />
                {BODY_LABELS[value]}
              </label>
            ))}
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-[1fr_11rem]">
          <div className="space-y-1">
            <Label htmlFor={ids.title}>Titre</Label>
            <Input
              id={ids.title}
              placeholder={template?.title ?? 'Résolution'}
              value={title}
              onChange={(event) => {
                setTitle(event.target.value)
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={ids.date}>Date d’effet</Label>
            <Input
              id={ids.date}
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value)
              }}
            />
          </div>
        </div>
        {create.error && (
          <FormError>
            {create.error instanceof ApiError
              ? create.error.message
              : 'Le brouillon n’a pas pu être créé.'}
          </FormError>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Annuler
          </Button>
          <Button
            type="button"
            disabled={create.isPending || !date}
            data-testid="resolution-creer"
            onClick={() => {
              create.mutate()
            }}
          >
            Créer le brouillon
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
